import React, { useState, useRef } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Switch,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import { Sms, Thermostat, WaterDrop, CheckCircleOutline, ErrorOutline, Pause, PlayArrow } from "@mui/icons-material";

const getTemperatureTypeLabel = (tempType) => {
  switch (tempType) {
    case "chambre_froide_positive":
      return "Chambre froide positive";
    case "chambre_froide_negative":
      return "Chambre froide négative";
    case "ambiante":
      return "Température ambiante";
    default:
      return tempType || "Température";
  }
};

function DeviceAlertRow({
  icon,
  label,
  unit,
  thresholdValue,
  smsActive,
  isPaused,
  onThresholdChange,
  onSmsToggle,
  onPause,
  isMobile,
}) {
  const [value, setValue] = useState(thresholdValue ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const debounceTimer = useRef(null);
  const isEditing = useRef(false);

  // Ne re-synchronise la valeur depuis le parent que si l'utilisateur n'est
  // pas en train d'éditer, pour ne pas écraser une saisie en cours quand la
  // page se rafraîchit.
  React.useEffect(() => {
    if (!isEditing.current) {
      setValue(thresholdValue ?? "");
    }
  }, [thresholdValue]);

  const commit = async (raw) => {
    const num = parseFloat(raw);
    if (isNaN(num)) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSaving(true);
    setError(false);
    try {
      const ok = await onThresholdChange(num);
      if (ok === false) {
        setError(true);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }
    } catch (e) {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    isEditing.current = true;
    setValue(v);
    setSaved(false);
    setError(false);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => commit(v), 800);
  };

  // Validation explicite : quand on quitte le champ.
  const handleBlur = () => {
    isEditing.current = false;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (value !== "" && parseFloat(value) !== (thresholdValue ?? NaN)) {
      commit(value);
    }
  };

  // Validation explicite : à la touche Entrée (déclenche le blur).
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const handlePause = async () => {
    setPauseLoading(true);
    try {
      await onPause(!isPaused);
    } finally {
      setPauseLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      backgroundColor: "var(--bg-card)",
      "& fieldset": { borderColor: "#fe3e26" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        {icon}
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        {isPaused ? (
          <Chip
            size="small"
            icon={<Pause sx={{ fontSize: "12px !important" }} />}
            label="En pause jusqu'à demain"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: "rgba(255, 152, 0, 0.15)",
              color: "#f57c00",
              "& .MuiChip-icon": { color: "#f57c00" },
            }}
          />
        ) : (
          <Chip
            size="small"
            label={smsActive ? "Alerte active" : "Alerte inactive"}
            sx={{
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: smsActive
                ? "var(--color-primary-lighter)"
                : "var(--bg-hover, rgba(255,255,255,0.06))",
              color: smsActive ? "var(--color-primary)" : "var(--text-secondary)",
            }}
          />
        )}
      </Box>

      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={isMobile ? 12 : 6} sm={6}>
          <TextField
            label="Seuil rouge"
            type="number"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            error={error}
            helperText={error ? "Non enregistré, réessayez" : undefined}
            size="small"
            fullWidth
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {saving ? (
                    <CircularProgress size={14} />
                  ) : saved ? (
                    <CheckCircleOutline sx={{ fontSize: 16, color: "var(--color-primary)" }} />
                  ) : error ? (
                    <ErrorOutline sx={{ fontSize: 16, color: "#fe3e26" }} />
                  ) : (
                    <Typography sx={{ fontSize: "0.75rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                      {unit}
                    </Typography>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
            Alerte SMS
          </Typography>
          <Switch
            size="small"
            checked={!!smsActive}
            onChange={onSmsToggle}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-primary)" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "var(--color-primary)",
              },
            }}
          />
        </Grid>
        {smsActive && (
          <Grid item xs="auto">
            <Button
              size="small"
              variant={isPaused ? "contained" : "outlined"}
              onClick={handlePause}
              disabled={pauseLoading}
              startIcon={
                pauseLoading
                  ? <CircularProgress size={12} />
                  : isPaused
                  ? <PlayArrow sx={{ fontSize: 14 }} />
                  : <Pause sx={{ fontSize: 14 }} />
              }
              sx={{
                fontSize: "0.72rem",
                px: 1,
                py: 0.25,
                minWidth: 0,
                borderColor: isPaused ? undefined : "var(--border-medium)",
                backgroundColor: isPaused ? "#f57c00" : undefined,
                color: isPaused ? "#fff" : "var(--text-secondary)",
                "&:hover": {
                  backgroundColor: isPaused ? "#e65100" : undefined,
                },
                whiteSpace: "nowrap",
              }}
            >
              {isPaused ? "Reprendre" : "Pause"}
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function SmsAlertsCard({
  devicesByType,
  deviceSmsStates,
  deviceTemperatureTypes,
  deviceThresholds = {},
  devicePauseStates = {},
  onSmsToggle,
  onSmsPause,
  onThresholdChange,
  isMobile,
}) {
  const temperatureDevices = Object.entries(devicesByType).flatMap(
    ([typeId, devices]) => {
      if (!devices || devices.length === 0) return [];
      return devices.filter((device) => {
        const deviceName = (device.name || "").toLowerCase();
        return (
          deviceName.includes("temp") ||
          deviceName.includes("chambre") ||
          deviceName.includes("froid") ||
          typeId.includes("temp")
        );
      });
    }
  );

  const waterDevices = Object.entries(devicesByType).flatMap(
    ([typeId, devices]) => {
      if (!devices || devices.length === 0) return [];
      return devices.filter((device) => {
        const deviceName = (device.name || "").toLowerCase();
        return (
          deviceName.includes("ade_pulse") ||
          deviceName.includes("mad_evo") ||
          deviceName.includes("cubic") ||
          deviceName.includes("sontex") ||
          deviceName.includes("superaqua") ||
          typeId.includes("water")
        );
      });
    }
  );

  const hasTemperature = temperatureDevices.length > 0;
  const hasWater = waterDevices.length > 0;

  if (!hasTemperature && !hasWater) return null;

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: "6px !important",
            border: "1px solid var(--border-subtle)",
            boxShadow: "var(--shadow-md)",
            backgroundColor: "var(--bg-card)",
          }}
        >
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Sms sx={{ color: "var(--color-primary)", fontSize: 20 }} />
              <Typography sx={{ fontSize: isMobile ? "0.95rem" : "1.05rem", fontWeight: 600 }}>
                Alertes SMS par équipement
              </Typography>
            </Box>

            {hasTemperature && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.25 }}>
                  <Thermostat sx={{ fontSize: 16, color: "var(--color-temperature)" }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
                    Température
                  </Typography>
                </Box>
                {temperatureDevices.map((device) => {
                  const smsKey = device.dev_eui;
                  const label =
                    getTemperatureTypeLabel(deviceTemperatureTypes[device.dev_eui]) ||
                    device.name ||
                    device.dev_eui;
                  return (
                    <DeviceAlertRow
                      key={smsKey}
                      icon={<Thermostat sx={{ fontSize: 18, color: "var(--color-temperature)" }} />}
                      label={label}
                      unit="°C"
                      thresholdValue={deviceThresholds[smsKey]}
                      smsActive={deviceSmsStates[smsKey]}
                      isPaused={devicePauseStates[smsKey] || false}
                      onThresholdChange={(val) => onThresholdChange(device.dev_eui, val, "temperature")}
                      onSmsToggle={() => onSmsToggle(device.dev_eui, deviceSmsStates[smsKey], "temperature")}
                      onPause={(shouldPause) => onSmsPause(device.dev_eui, "temperature", shouldPause)}
                      isMobile={isMobile}
                    />
                  );
                })}
              </>
            )}

            {hasTemperature && hasWater && <Divider sx={{ my: 1.5 }} />}

            {hasWater && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.25 }}>
                  <WaterDrop sx={{ fontSize: 16, color: "var(--color-water, #2196f3)" }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
                    Compteurs eau
                  </Typography>
                </Box>
                {waterDevices.map((device) => {
                  const smsKey = `water_${device.dev_eui}`;
                  return (
                    <DeviceAlertRow
                      key={smsKey}
                      icon={<WaterDrop sx={{ fontSize: 18, color: "var(--color-water, #2196f3)" }} />}
                      label={device.name || device.dev_eui}
                      unit="L/j"
                      thresholdValue={deviceThresholds[smsKey]}
                      smsActive={deviceSmsStates[smsKey]}
                      isPaused={devicePauseStates[smsKey] || false}
                      onThresholdChange={(val) => onThresholdChange(device.dev_eui, val, "water")}
                      onSmsToggle={() => onSmsToggle(device.dev_eui, deviceSmsStates[smsKey], "water")}
                      onPause={(shouldPause) => onSmsPause(device.dev_eui, "water", shouldPause)}
                      isMobile={isMobile}
                    />
                  );
                })}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default SmsAlertsCard;
